import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Icon from '../../../controls/Icon';
import CreateLayoutItemModal from './modals/CreateLayoutItemModal.jsx';
import GenericDropdown from '../../../controls/GenericDropdown';
import SearchBar from '../../../controls/SearchBar';
import GenericList from '../../../controls/List';
import { useUIConfig } from './Context';
import PubSub from '../../../../services/PubSub.js';
import BUILD_TIME_CONFIG_TYPES from '../../../../config/buildTimeConfigTypes.js';

const ListFilter = ({
    type,
    allowedGroups,
    isDraggable,
    onSelect,
    defaultValue,
    createNew = true,
    isTopLevel = false,
}) => {
    const ref = useRef();
    const [translate] = useTranslation();
    const [filter, setFilter] = useState(defaultValue || null);
    const [selectedListItem, setSelectedListItem] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [preselectedCopyItem, setPreselectedCopyItem] = useState(null);

    const [searchPrompt, setSearchPrompt] = useState('');

    const [showCreateModal, setShowCreateModal] = useState(false);
    const { add, components, blocks, applets, jsxComponents, setBasePath, setPropEditorPath, drillDownLayoutItem, selectDrillDownLayoutItem, getFilteredData, getMergedTemplate, currentOverrides, navigateToLayout } = useUIConfig();

    const history = useHistory();
    const location = useLocation();

    const addLayoutItem = useCallback(({ layoutItemName, layoutItem, fromExisting = false }) => {
        if (applets[layoutItemName] || blocks[layoutItemName] || components[layoutItemName]) {
            PubSub.publish("show_info", translate("OTM_LAYOUT_ITEM_ALREADY_DEFINED"));
            return;
        }

        if (fromExisting) {
            switch (type) {
                case BUILD_TIME_CONFIG_TYPES.APPLETS: {
                    layoutItem = applets[layoutItem];
                    break;
                }
                case BUILD_TIME_CONFIG_TYPES.BLOCKS: {
                    layoutItem = blocks[layoutItem];
                    break;
                }
                case BUILD_TIME_CONFIG_TYPES.COMPONENTS:
                default: {
                    layoutItem = components[layoutItem];
                }
            }
        }
        add([type, layoutItemName], layoutItem, true);
        setSearchPrompt("");
        ref.current.resetSearch();
        setShowCreateModal(false);

        navigateToLayout({ history, layoutType: type, layoutName: layoutItemName, state: { keepBcContext: false } });
        setSelectedListItem(layoutItemName);
    }, [type, add, components, blocks, applets, history, navigateToLayout, translate]);

    const selectCallback = useCallback((_dataElement, value) => {
        setSelectedListItem(value);

        if (onSelect) {
            onSelect(value);
        }
        else {
            const initialPath = [type, value];
            const { path } = getMergedTemplate(initialPath, currentOverrides);
            setBasePath(path);
            setPropEditorPath(path);

            if (isTopLevel) {
                navigateToLayout({ history, layoutType: type, layoutName: value });
            }
            else {
                selectDrillDownLayoutItem({ type, name: value })
            }
        }
    }, [onSelect, setBasePath, setPropEditorPath, getMergedTemplate, currentOverrides, type, isTopLevel, history, navigateToLayout, selectDrillDownLayoutItem]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);

        if (!selectedListItem) {
            setSelectedListItem(drillDownLayoutItem);
        }

        if (queryParams.get('create') === 'true') {
            setPreselectedCopyItem(location.state?.preselectedCopyItem);
            setShowCreateModal(true);

            queryParams.delete('create');
            history.replace({
                search: queryParams.toString(),
            });
        }
    }, [location, history, selectedListItem, drillDownLayoutItem]);

    useEffect(() => {
        setFilteredData(getFilteredData(type, filter, allowedGroups).filter(val => val.name.toLowerCase().includes(searchPrompt.toLowerCase())));
    }, [type, filter, allowedGroups, getFilteredData, searchPrompt]);

    const dropdownOptions = useMemo(() => {
        return jsxComponents.filter((metadata) => {
            if (allowedGroups?.length > 0) {
                return (metadata.groups || []).filter((group) => allowedGroups.includes(group)).length > 0;
            }

            return metadata.visible === true;
        }).map(({ name }) => ({
            value: name,
        }));
    }, [jsxComponents, allowedGroups]);

    const renderSearchBar = useMemo(() => {
        return <div>
            <SearchBar
                ref={ref}
                size="sm"
                initialValue={searchPrompt}
                placeholder={translate("OTM_SEARCH")}
                onChange={(value) => setSearchPrompt(value)} />
        </div>
    }, [searchPrompt, translate]);

    const renderHeading = useMemo(() => {
        return <div className="rowHeading py-1 sticky-top mb-3">
            <div className="col me-auto">
                {renderSearchBar}
            </div>
            {type === BUILD_TIME_CONFIG_TYPES.COMPONENTS && <div className="col-auto">
                <GenericDropdown
                    options={dropdownOptions}
                    addEmptyOption={true}
                    defaultValue={defaultValue}
                    placeholder={translate("OTM_FILTER_BY_TYPE_PLACEHOLDER")}
                    size="sm"
                    onSelect={(value) => {
                        setFilter(value === '' ? null : value);
                    }}
                />
            </div>
            }
            {createNew && <div className="col-auto">
                <Button
                    variant='outline-primary'
                    className='p-0 border-0'
                    onClick={() => setShowCreateModal(true)}
                >
                    <Icon icon='Add' />
                </Button>
            </div>
            }
        </div >
    }, [createNew, defaultValue, type, dropdownOptions, renderSearchBar, translate]);

    return (
        <>
            {renderHeading}
            <GenericList
                data={filteredData}
                attr='name'
                selectedElement={isDraggable ? null : selectedListItem}
                onClick={isDraggable ? null : selectCallback}
                drag={isDraggable ? { type: 'LayoutItem', } : null}
                listItemCss='position-static'
                css='mb-2'
            />

            <CreateLayoutItemModal
                layoutItemType={type}
                show={showCreateModal}
                preselectedCopyItem={preselectedCopyItem}
                defaultValue={filter}
                onCreate={({ layoutItemName, layoutItem, fromExisting }) => { addLayoutItem({ layoutItemName, layoutItem, fromExisting }) }}
                onCancel={() => setShowCreateModal(false)}
            />
        </>
    );
};

export default ListFilter;